/**
 * HenniPenny NILM workbench panels — the field-specific rich content the shared
 * `WorkbenchShell` injects into the HenniPenny manifest's tab slots.
 *
 * `useNilmWorkbench()` owns one shared run state and returns the four tab panels
 * (disaggregation input/run · precision·recall·F1 · confusion matrix · model & dataset
 * provenance). Metrics are computed deterministically in the browser by the exact port of
 * the server-side `ClassificationMetricsAnalyzer` (see `./metrics`) — the panel is a preview
 * of the same figures the sealed `evidence.v1` run certifies, not a second source of truth.
 */
import { type CSSProperties, type ReactNode, useMemo, useState } from 'react';
import { Activity, AlertTriangle, Database, FlaskConical, Grid3x3, Play, RefreshCcw, ShieldCheck, Zap } from 'lucide-react';
import {
  NILM_ANALYZER_NOTES,
  computeClassificationMetrics,
  parseLabelCsv,
  type ClassificationMetrics,
} from './metrics';
import { NILM_SAMPLE_BENCHMARK, NILM_SAMPLE_LABELS_CSV, NILM_SAMPLE_PREDICTIONS_CSV } from './sample';

const panelStyle = {
  border: '1px solid var(--sp-border)',
  borderRadius: 'var(--sp-radius-lg)',
  background: 'var(--sp-bg-elev)',
  padding: 'var(--sp-space-4)',
} satisfies CSSProperties;

const subtlePanelStyle = {
  border: '1px solid var(--sp-border)',
  borderRadius: 'var(--sp-radius)',
  background: 'var(--sp-bg)',
  padding: 'var(--sp-space-3)',
} satisfies CSSProperties;

function fmt(value: number): string {
  return value.toFixed(3);
}

function SectionTitle({ icon: Icon, eyebrow, title }: { icon: typeof FlaskConical; eyebrow: string; title: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 'var(--sp-space-3)' }}>
      <Icon size={18} style={{ color: 'var(--sp-text-muted)', marginTop: 2, flexShrink: 0 }} />
      <div>
        <div className="sp-eyebrow">{eyebrow}</div>
        <h2 className="sp-display" style={{ margin: '4px 0 0', fontSize: 18, fontWeight: 400 }}>
          {title}
        </h2>
      </div>
    </div>
  );
}

function StatTile({ label, value, tone }: { label: string; value: string; tone?: 'ok' | 'warn' }) {
  const color = tone === 'ok' ? 'var(--sp-ok)' : tone === 'warn' ? 'var(--sp-warn)' : 'var(--sp-text)';
  return (
    <div style={subtlePanelStyle}>
      <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ marginTop: 5, fontSize: 20, fontWeight: 700, color, overflowWrap: 'anywhere' }}>{value}</div>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <section style={panelStyle}>
      <p style={{ margin: 0, color: 'var(--sp-text-muted)', lineHeight: 1.6 }}>{message}</p>
    </section>
  );
}

function AnalyzerNotes() {
  return (
    <div style={subtlePanelStyle}>
      <div className="sp-eyebrow" style={{ fontSize: 9.5 }}>
        Analyzer limitations (verbatim)
      </div>
      <p style={{ margin: '8px 0 0', color: 'var(--sp-text-muted)', fontSize: 12.5, lineHeight: 1.6 }}>
        {NILM_ANALYZER_NOTES}
      </p>
    </div>
  );
}

// --------------------------------------------------------------------------- #
// Tab panels (pure, driven by the shared state the hook holds)
// --------------------------------------------------------------------------- #
function DisaggregationPanel({
  predictionsText,
  truthText,
  error,
  result,
  onPredictionsChange,
  onTruthChange,
  onLoadSample,
  onRun,
  onClear,
}: {
  predictionsText: string;
  truthText: string;
  error: string | null;
  result: ClassificationMetrics | null;
  onPredictionsChange: (value: string) => void;
  onTruthChange: (value: string) => void;
  onLoadSample: () => void;
  onRun: () => void;
  onClear: () => void;
}) {
  return (
    <section style={panelStyle}>
      <SectionTitle icon={Zap} eyebrow="Run disaggregation" title="Predictions vs ground truth" />
      <p style={{ margin: '-4px 0 14px', color: 'var(--sp-text-muted)', fontSize: 13, lineHeight: 1.6 }}>
        Paste your appliance-level predictions and the benchmark's ground-truth labels, or load the synthetic
        sample. The workbench joins them on <code>sample_id</code> and computes the confusion matrix and
        precision / recall / F1 deterministically — the same math the sealed evidence run certifies. It scores
        the metrics, never the model.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 12 }}>
        <label style={{ display: 'block' }}>
          <span className="sp-label">Predictions CSV (sample_id, predicted_label, score)</span>
          <textarea
            className="sp-input"
            rows={9}
            value={predictionsText}
            onChange={(event) => onPredictionsChange(event.currentTarget.value)}
            aria-label="Predictions CSV"
            spellCheck={false}
            style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 12, resize: 'vertical' }}
          />
        </label>
        <label style={{ display: 'block' }}>
          <span className="sp-label">Benchmark labels CSV (sample_id, true_label)</span>
          <textarea
            className="sp-input"
            rows={9}
            value={truthText}
            onChange={(event) => onTruthChange(event.currentTarget.value)}
            aria-label="Benchmark labels CSV"
            spellCheck={false}
            style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 12, resize: 'vertical' }}
          />
        </label>
      </div>

      <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="sp-btn" type="button" onClick={onLoadSample} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <RefreshCcw size={15} />
          Use the synthetic sample
        </button>
        <button
          className="sp-btn sp-btn-primary"
          type="button"
          onClick={onRun}
          disabled={!predictionsText.trim() || !truthText.trim()}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
        >
          <Play size={15} />
          Run disaggregation metrics
        </button>
        {result ? (
          <button className="sp-btn" type="button" onClick={onClear} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            Clear result
          </button>
        ) : null}
      </div>

      {error ? (
        <div role="alert" style={{ marginTop: 12, color: 'var(--sp-error)', fontSize: 13, lineHeight: 1.5 }}>
          Could not score this pair. {error}
        </div>
      ) : null}

      {result ? (
        <div style={{ marginTop: 14, ...subtlePanelStyle }}>
          <div className="sp-eyebrow" style={{ fontSize: 9.5 }}>
            Latest run
          </div>
          <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'baseline' }}>
            <div style={{ fontSize: 20, fontWeight: 700 }}>
              Accuracy {fmt(result.accuracy)} · Macro-F1 {fmt(result.macroF1)}
            </div>
            <div className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 11 }}>
              {result.nCorrect}/{result.nEvaluated} correct · {result.nClasses} appliances
            </div>
          </div>
          <p style={{ margin: '8px 0 0', color: 'var(--sp-text-muted)', fontSize: 12.5, lineHeight: 1.6 }}>
            See the <strong>Precision · Recall · F1</strong> and <strong>Confusion matrix</strong> tabs for the full
            breakdown. A sealed, replayable <code>evidence.v1</code> bundle for this run is produced by
            <code> researchharness classification-evidence</code>; its scientific verdict floors to
            <code> inconclusive</code> until a reviewer signs off.
          </p>
        </div>
      ) : null}

      <div style={{ marginTop: 12 }}>
        <AnalyzerNotes />
      </div>
    </section>
  );
}

function MetricsPanel({ result }: { result: ClassificationMetrics | null }) {
  if (!result) {
    return <EmptyState message="Run the synthetic sample (or your own predictions) on the Disaggregation tab to see precision, recall, and F1 here." />;
  }
  return (
    <>
      <section style={panelStyle}>
        <SectionTitle icon={Activity} eyebrow="Figures of merit" title="Precision · Recall · F1" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
          <StatTile label="Accuracy" value={fmt(result.accuracy)} tone="ok" />
          <StatTile label="Balanced accuracy" value={fmt(result.balancedAccuracy)} />
          <StatTile label="Macro precision" value={fmt(result.macroPrecision)} />
          <StatTile label="Macro recall" value={fmt(result.macroRecall)} />
          <StatTile label="Macro F1" value={fmt(result.macroF1)} />
          <StatTile label="Weighted F1" value={fmt(result.weightedF1)} />
        </div>
        <div className="mono" style={{ marginTop: 12, color: 'var(--sp-text-dim)', fontSize: 11 }}>
          {result.nEvaluated} evaluated · {result.nBenchmarkSamples} benchmark samples · {result.nPredictions} predictions
          {result.missingPredictionIds.length ? ` · ${result.missingPredictionIds.length} benchmark sample(s) with no prediction` : ''}
          {result.extraPredictionIds.length ? ` · ${result.extraPredictionIds.length} prediction(s) outside the benchmark` : ''}
        </div>
        {result.flags.length ? (
          <div style={{ marginTop: 10, color: 'var(--sp-warn)', fontSize: 12.5 }}>Flags: {result.flags.join(', ')}</div>
        ) : (
          <div style={{ marginTop: 10, color: 'var(--sp-ok)', fontSize: 12.5 }}>No coverage or balance flags raised.</div>
        )}
      </section>

      <section style={panelStyle}>
        <SectionTitle icon={Database} eyebrow="Per-appliance breakdown" title="One-vs-rest precision / recall / F1" />
        <div style={{ overflowX: 'auto' }}>
          <table className="mono" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 460 }}>
            <thead>
              <tr>
                {['Appliance', 'Precision', 'Recall', 'F1', 'Support', 'TP', 'FP', 'FN'].map((head) => (
                  <th
                    key={head}
                    style={{
                      textAlign: head === 'Appliance' ? 'left' : 'right',
                      padding: '7px 10px',
                      borderBottom: '1px solid var(--sp-border)',
                      color: 'var(--sp-text-dim)',
                      textTransform: 'uppercase',
                      fontSize: 10,
                      letterSpacing: '0.04em',
                    }}
                  >
                    {head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.labels.map((label) => {
                const row = result.perClass[label];
                return (
                  <tr key={label}>
                    <td style={{ textAlign: 'left', padding: '7px 10px', borderBottom: '1px solid var(--sp-border)', fontWeight: 700 }}>{label}</td>
                    <td style={{ textAlign: 'right', padding: '7px 10px', borderBottom: '1px solid var(--sp-border)' }}>{fmt(row.precision)}</td>
                    <td style={{ textAlign: 'right', padding: '7px 10px', borderBottom: '1px solid var(--sp-border)' }}>{fmt(row.recall)}</td>
                    <td style={{ textAlign: 'right', padding: '7px 10px', borderBottom: '1px solid var(--sp-border)' }}>{fmt(row.f1)}</td>
                    <td style={{ textAlign: 'right', padding: '7px 10px', borderBottom: '1px solid var(--sp-border)', color: 'var(--sp-text-muted)' }}>{row.support}</td>
                    <td style={{ textAlign: 'right', padding: '7px 10px', borderBottom: '1px solid var(--sp-border)', color: 'var(--sp-text-muted)' }}>{row.tp}</td>
                    <td style={{ textAlign: 'right', padding: '7px 10px', borderBottom: '1px solid var(--sp-border)', color: 'var(--sp-text-muted)' }}>{row.fp}</td>
                    <td style={{ textAlign: 'right', padding: '7px 10px', borderBottom: '1px solid var(--sp-border)', color: 'var(--sp-text-muted)' }}>{row.fn}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 12 }}>
          <AnalyzerNotes />
        </div>
      </section>
    </>
  );
}

function ConfusionPanel({ result }: { result: ClassificationMetrics | null }) {
  if (!result) {
    return <EmptyState message="Run a disaggregation first — the confusion matrix appears here once predictions and ground truth are scored." />;
  }
  const { labels, confusion } = result;
  const maxCount = Math.max(1, ...labels.flatMap((t) => labels.map((p) => confusion[t][p])));
  return (
    <section style={panelStyle}>
      <SectionTitle icon={Grid3x3} eyebrow="Error structure" title="Confusion matrix" />
      <p style={{ margin: '-4px 0 14px', color: 'var(--sp-text-muted)', fontSize: 13, lineHeight: 1.6 }}>
        Rows are the ground-truth appliance; columns are what the model predicted. The diagonal is correct; every
        off-diagonal cell is a specific confusion (e.g. a fridge cycle read as a microwave).
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table className="mono" style={{ borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead>
            <tr>
              <th style={{ padding: '7px 10px', color: 'var(--sp-text-dim)', fontSize: 10, textTransform: 'uppercase', textAlign: 'left' }}>
                true \ pred
              </th>
              {labels.map((p) => (
                <th key={p} style={{ padding: '7px 12px', color: 'var(--sp-text-dim)', fontSize: 10, textTransform: 'uppercase', textAlign: 'center' }}>
                  {p}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {labels.map((t) => (
              <tr key={t}>
                <th style={{ padding: '7px 10px', color: 'var(--sp-text-muted)', textAlign: 'left', fontWeight: 700 }}>{t}</th>
                {labels.map((p) => {
                  const count = confusion[t][p];
                  const isDiagonal = t === p;
                  const intensity = count === 0 ? 0 : 0.12 + 0.5 * (count / maxCount);
                  const background = count === 0
                    ? 'transparent'
                    : isDiagonal
                      ? `color-mix(in srgb, var(--sp-ok) ${Math.round(intensity * 100)}%, transparent)`
                      : `color-mix(in srgb, var(--sp-warn) ${Math.round(intensity * 100)}%, transparent)`;
                  return (
                    <td
                      key={`${t}-${p}`}
                      style={{
                        padding: '10px 12px',
                        textAlign: 'center',
                        border: '1px solid var(--sp-border)',
                        background,
                        fontWeight: isDiagonal ? 700 : 400,
                        color: count === 0 ? 'var(--sp-text-dim)' : 'var(--sp-text)',
                      }}
                    >
                      {count}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mono" style={{ marginTop: 12, color: 'var(--sp-text-dim)', fontSize: 11 }}>
        {result.nCorrect}/{result.nEvaluated} on the diagonal · green = correct, amber = confusion
      </div>
    </section>
  );
}

function ProvenancePanel({ result }: { result: ClassificationMetrics | null }) {
  return (
    <>
      <section style={panelStyle}>
        <SectionTitle icon={ShieldCheck} eyebrow="What data, what model" title="Model & dataset provenance" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: 10 }}>
          <div style={subtlePanelStyle}>
            <div className="sp-eyebrow" style={{ fontSize: 9.5 }}>
              Dataset
            </div>
            <div style={{ marginTop: 6, fontWeight: 700 }}>{NILM_SAMPLE_BENCHMARK.name}</div>
            <div style={{ marginTop: 6, color: 'var(--sp-text-muted)', fontSize: 12.5, lineHeight: 1.6 }}>
              Release <code>{NILM_SAMPLE_BENCHMARK.release}</code> · {NILM_SAMPLE_BENCHMARK.publisher}. {NILM_SAMPLE_BENCHMARK.note}
            </div>
            <div style={{ marginTop: 8, color: 'var(--sp-text-dim)', fontSize: 11.5 }}>
              Real benchmark pin: <code>{NILM_SAMPLE_BENCHMARK.realPin}</code>.
            </div>
          </div>
          <div style={subtlePanelStyle}>
            <div className="sp-eyebrow" style={{ fontSize: 9.5 }}>
              Model run
            </div>
            <div style={{ marginTop: 6, fontWeight: 700 }}>Researcher-reported</div>
            <div style={{ marginTop: 6, color: 'var(--sp-text-muted)', fontSize: 12.5, lineHeight: 1.6 }}>
              The model that produced these predictions is taken at face value — only the join-and-count is
              tool-computed. The workbench cannot detect train/test leakage and makes no claim about model quality,
              generalization, or fitness for deployment.
            </div>
          </div>
        </div>
        <div style={{ marginTop: 10, ...subtlePanelStyle }}>
          <div className="sp-eyebrow" style={{ fontSize: 9.5 }}>
            Evidence bundle honesty
          </div>
          <ul style={{ margin: '8px 0 0', paddingLeft: 18, display: 'grid', gap: 5, color: 'var(--sp-text-muted)', fontSize: 12.5, lineHeight: 1.55 }}>
            <li>A run emits the canonical <code>evidence.v1</code> bundle via <code>researchharness classification-evidence</code>, producer <code>hennipenny-nilm-researchharness</code>.</li>
            <li>The scientific verdict floors to <code>inconclusive</code>: a metrics readout is not a scientific verdict until a reviewer signs off.</li>
            <li>The provenance <code>receipt</code> is <code>null</code> — the trust-root slot a signer fills later. Nothing is auto-upgraded.</li>
            <li>Synthetic sample runs stay labelled synthetic; they prove the workflow and the math, not appliance-level adoption.</li>
          </ul>
        </div>
        <div style={{ marginTop: 10 }}>
          <AnalyzerNotes />
        </div>
      </section>

      <section style={panelStyle}>
        <SectionTitle icon={AlertTriangle} eyebrow="Claim boundaries" title="What this workbench will not do" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
          {[
            'It scores appliance-classification metrics against a labelled benchmark — it does not certify the disaggregation model itself.',
            'It does not rank models, auto-tune thresholds, or decide whether the metrics are good enough — that is the reviewer’s call.',
            'Coverage gaps are reported as counts and flags, never folded into the confusion matrix as assumed errors.',
            result && result.flags.length
              ? `This run raised: ${result.flags.join(', ')}. Read them before citing the numbers.`
              : 'Real appliance/energy data stays analyst-declared until a reviewer confirms; a sample run is not adoption evidence.',
          ].map((item) => (
            <div key={item} style={subtlePanelStyle}>
              <span style={{ color: 'var(--sp-text-muted)', fontSize: 13, lineHeight: 1.55 }}>{item}</span>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

export type NilmWorkbenchPanels = {
  disaggregation: ReactNode;
  metrics: ReactNode;
  confusion: ReactNode;
  provenance: ReactNode;
};

/** Own one shared NILM run state; return the four HenniPenny tab panels. */
export function useNilmWorkbench(): NilmWorkbenchPanels {
  const [predictionsText, setPredictionsText] = useState('');
  const [truthText, setTruthText] = useState('');
  const [result, setResult] = useState<ClassificationMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadSample = () => {
    setPredictionsText(NILM_SAMPLE_PREDICTIONS_CSV);
    setTruthText(NILM_SAMPLE_LABELS_CSV);
    setError(null);
    setResult(null);
  };

  const run = () => {
    try {
      const predictions = parseLabelCsv(predictionsText, 'sample_id', 'predicted_label');
      const truth = parseLabelCsv(truthText, 'sample_id', 'true_label');
      setResult(computeClassificationMetrics(predictions.map, truth.map));
      setError(null);
    } catch (caught) {
      setResult(null);
      setError(caught instanceof Error ? caught.message : 'Check the CSV shape and try again.');
    }
  };

  const clear = () => {
    setResult(null);
    setError(null);
  };

  return useMemo<NilmWorkbenchPanels>(
    () => ({
      disaggregation: (
        <DisaggregationPanel
          predictionsText={predictionsText}
          truthText={truthText}
          error={error}
          result={result}
          onPredictionsChange={setPredictionsText}
          onTruthChange={setTruthText}
          onLoadSample={loadSample}
          onRun={run}
          onClear={clear}
        />
      ),
      metrics: <MetricsPanel result={result} />,
      confusion: <ConfusionPanel result={result} />,
      provenance: <ProvenancePanel result={result} />,
    }),
    // run/loadSample/clear are stable closures over the state below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [predictionsText, truthText, error, result],
  );
}
